import type { SxProps, Theme } from '@mui/material/styles';
import { DEFAULT_SKIN } from '../../constants/marketplace';
import type { SkinStylesMap } from '../../hooks/useSkinStyles';
import { SKIN } from '../../types/enums';
import { neobetFormFieldSx } from './neobetFormFields';

export const zeroXCashierStyles = {
  root: { gap: 2, minWidth: 0, flex: '1 0 auto' },
  field: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'neutral.600',
      '&:not(.Mui-error) fieldset': { borderColor: 'neutral.500' },
      '&:hover:not(.Mui-error) fieldset': { borderColor: 'neutral.300' },
      '&.Mui-focused:not(.Mui-error) fieldset': { borderColor: 'secondary.main' },
    },
    '& .MuiInputLabel-root': { color: 'text.secondary' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'text.primary' },
    '& .MuiOutlinedInput-input': { color: 'text.primary' },
  },
  currencyField: {
    '& .MuiOutlinedInput-root': { minHeight: 48 },
    '& .MuiSelect-select': { px: 1.5, py: '14px', textAlign: 'left' },
    '& .MuiSelect-icon': { color: 'text.secondary' },
  },
  summary: { gap: 2.5, minWidth: 0 },
  received: {
    gap: 0.75,
    p: { xs: 2, md: 2.5 },
    borderRadius: 2,
    backgroundColor: 'neutral.600',
  },
  label: {
    typography: 'caption',
    color: 'neutral.200',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    lineHeight: 1.4,
  },
  amount: {
    typography: 'h1',
    fontSize: { xs: '2.25rem', sm: '2.5rem' },
    fontWeight: 800,
    color: 'neutral.25',
    letterSpacing: '-0.025em',
    lineHeight: 1.1,
    minWidth: 0,
    overflowWrap: 'anywhere',
    fontVariantNumeric: 'tabular-nums',
  },
  asset: { typography: 'h4', fontWeight: 600, letterSpacing: 0, color: 'neutral.100' },
  breakdown: { gap: 1.5, px: { xs: 0.5, md: 1 } },
  details: { m: 0, display: 'flex', flexDirection: 'column', gap: 1.25 },
  row: { display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 2, alignItems: 'baseline' },
  debitRow: { pb: 1.25, mb: 0.25, borderBottom: '1px solid', borderColor: 'divider' },
  debitTerm: { typography: 'body2Compact', fontWeight: 600, color: 'neutral.100' },
  debitValue: {
    m: 0,
    typography: 'h4',
    fontSize: '1.25rem',
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: 0,
    color: 'neutral.25',
    textAlign: 'right',
    overflowWrap: 'anywhere',
    fontVariantNumeric: 'tabular-nums',
  },
  term: { typography: 'body3', letterSpacing: 0, color: 'neutral.200' },
  value: {
    m: 0,
    typography: 'subtitle2',
    fontWeight: 600,
    color: 'neutral.50',
    textAlign: 'right',
    overflowWrap: 'anywhere',
    fontVariantNumeric: 'tabular-nums',
  },
  valueUnit: { typography: 'body3', fontWeight: 400, letterSpacing: 0, color: 'neutral.200' },
  destinationDetails: {
    m: 0,
    p: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 1.75,
    borderRadius: 2,
    backgroundColor: 'neutral.600',
  },
  destination: { display: 'flex', flexDirection: 'column', gap: 0.75 },
  destinationValue: {
    m: 0,
    typography: 'subtitle2',
    fontWeight: 600,
    color: 'neutral.100',
    overflowWrap: 'anywhere',
    userSelect: 'all',
  },
  addressValue: {
    typography: 'body2Compact',
    fontFamily: 'monospace',
    fontWeight: 500,
    color: 'neutral.25',
    lineHeight: 1.7,
  },
  memoValue: { typography: 'h4', fontWeight: 700, letterSpacing: 0, color: 'neutral.25' },
  feeHelp: { typography: 'body3', fontWeight: 400, letterSpacing: 0, lineHeight: 1.5, color: 'neutral.200' },
  actions: { gap: 1, mt: 'auto', flexShrink: 0 },
  stage: { gap: 2, outline: 'none' },
} as const;

export const zeroXCashierFieldStyles: SkinStylesMap<Record<'text' | 'select' | 'formInset', SxProps<Theme>>> = {
  [DEFAULT_SKIN]: {
    text: zeroXCashierStyles.field,
    select: [zeroXCashierStyles.field, zeroXCashierStyles.currencyField],
    formInset: { pt: 1 },
  },
  [SKIN.NEOBET]: { ...neobetFormFieldSx, formInset: { pt: 0 } },
};
